import { HttpClient } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { OktaAuthService } from '@okta/okta-angular';
import { BehaviorSubject, from, Observable, of } from 'rxjs';
import { concatMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { v4 as uuid } from 'uuid';
import { CustomerResponse, Customers, Facilities, BusinessUnits, } from '../model/customer';
import { FscAuthorizedMenusResponse, Module, NavItem, UserActions } from '../model/nav-item';
import { CustomerService } from './customer.service';
import { FclLoginService } from './fcllogin.service';
import { BuFacility } from '../model/generic';
import { SortByPipe } from '../pipes/sort-by.pipe';
import { Router, NavigationEnd, NavigationStart } from '@angular/router';
import { filter } from 'rxjs/operators';

@Injectable({
    providedIn: 'root'
})
export class NavService {
    public appDrawer: any;
    navItems: NavItem[] = [];
    navItems$ = new BehaviorSubject(this.navItems);
    fscAuthorizedMenusResponse: FscAuthorizedMenusResponse;
    modules: Module[] = [];
    modules$ = new BehaviorSubject(this.modules);
    defaultModules: Module[] = [];
    defaultModules$ = new BehaviorSubject(this.defaultModules);
    selectedModule = '';
    userActions: UserActions[] = [];
    customers: Customers[] = [];
    selectedCustomer: Customers;
    selectedFacility: Facilities;
    selectedBusinessUnit: BusinessUnits;
    selectedBusinessFacility: BuFacility;
    selectedMenuId = '';
    userType: string;
    buFacility: BuFacility = new BuFacility();
    buFacwAccess: BuFacility = new BuFacility();
    buFacwAccessArr: BuFacility[] = [];
    oktaState: OktaAuthService = null;
    oktaState$ = new BehaviorSubject(this.oktaState);
    
    userCustomers = new BehaviorSubject<Customers[]>([]);
    userCustomers$ = this.userCustomers.asObservable();
    homeClicked = false;
    navigationPath = '';
    public history: string[] = [];
    private currentUrl: string = '';

    constructor(
        private http: HttpClient,
        private fclLoginService: FclLoginService,
        private oktaAuth: OktaAuthService,
        private customerService: CustomerService,
        private router: Router) {
    }

    public hasHistory(): boolean {
        const length = this.history.length;
        return length > 0 ? true : false;

    }

    public getPreviousUrl(): string | null {
        return localStorage.getItem('previousUrl');
        // const length = this.history.length;
        // return length > 1 ? this.history[length] : null;
    }

    isUrlRefreshed(): boolean {
        const previousUrl = this.getPreviousUrl();
        return previousUrl === null || previousUrl === this.currentUrl;
        // return this.currentUrl === this.getPreviousUrl();
    }

    isUrlDifferentFromPrevious(url: string): boolean {
        return this.currentUrl !== url;
    }

    /**
     * Sets the previous route for back navigation
     * @param route The route to store as previous route
     */
    public setPreviousRoute(route: string): void {
        localStorage.setItem('previousRoute', route);
    }

    /**
     * Gets the stored previous route
     * @returns The previous route or null if not set
     */
    public getPreviousRoute(): string | null {
        return localStorage.getItem('previousRoute');
    }

    /**
     * Navigates back to the previous route if available
     * @param fallbackRoute Optional fallback route if no previous route is stored
     */
    public goToPreviousRoute(fallbackRoute?: string): void {
        const previousRoute = this.getPreviousRoute();
        if (previousRoute) {
            localStorage.removeItem('previousRoute'); // Clean up after use
            this.router.navigateByUrl(previousRoute);
        } else if (fallbackRoute) {
            this.router.navigateByUrl(fallbackRoute);
        } else {
            // Use browser's back button as last resort
            window.history.back();
        }
    }

    /**
     * Sets the active tab index for restoration
     * @param activeTab The tab index to store
     */
    public setActiveTab(activeTab: string): void {
        localStorage.setItem('activeTab', activeTab.toString());
    }

    /**
     * Gets the stored active tab index
     * @returns The active tab index or 0 if not set
     */
    public getActiveTab(): string | null {
        return localStorage.getItem('activeTab');
    }

    /**
     * Restores the active tab and cleans up storage
     * @param fallbackTab Optional fallback tab index if no stored tab is found
     * @returns The active tab index to restore
     */
    public goToActiveTab(fallbackTab?: string): string {
        const activeTab = this.getActiveTab();
        if (localStorage.getItem('activeTab')) {
            localStorage.removeItem('activeTab'); // Clean up after use
            return activeTab;
        } else if (fallbackTab !== undefined) {
            return fallbackTab;
        } else {
            return null; // Default to first tab
        }
    }

    /**
     * Clears all navigation state from localStorage
     * Optimized method to remove both previousRoute and activeTab in one call
     */
    public clearNavigationState(): void {
        localStorage.removeItem('previousRoute');
        localStorage.removeItem('activeTab');
    }

    public closeNav() {
        this.appDrawer.close();
    }

    public openNav() {
        this.appDrawer.open();
    }
    public toggleNav() {
        this.appDrawer.toggle();
    }

    changeUserCustomers(data: Customers[]) {
        this.userCustomers.next(data)
    }

    public setMenuName(menuName: string){
        sessionStorage.setItem('selected_menu_name', menuName);
    }
    public setMenuPermissions(menuId: string) {
        if (menuId !== undefined) {
            this.selectedMenuId = menuId;
            localStorage.setItem('selected_menu', menuId);
        } else {
            const currentMenuID = localStorage.getItem('selected_menu');
            this.selectedMenuId = currentMenuID;
        }
        const selectedModule = this.getSelectedModule();
        const moduleObj: Module[] = this.getLocalModules();
        if (moduleObj !== undefined) {
            const currentmodule: Module = moduleObj.find(module => module.moduleName === selectedModule);
            // Recurse the FULL navItem tree (any depth) — menu items can be nested
            // arbitrarily deep (e.g. Operations > Reverse Logistics > Execution > Scans),
            // not just the two levels (top-level item + its direct children) the old
            // loop checked, which silently dropped permissions for deeper menus.
            let currentMenu = currentmodule?.navItems !== undefined
                ? this.findMenuById(currentmodule.navItems, menuId)
                : undefined;
            // Fall back to searching every module in case the selected module no
            // longer matches (e.g. the menu moved to a different top-level module).
            if (currentMenu === undefined && moduleObj !== undefined) {
                for (const mod of moduleObj) {
                    if (mod.navItems === undefined) { continue; }
                    currentMenu = this.findMenuById(mod.navItems, menuId);
                    if (currentMenu !== undefined) { break; }
                }
            }
            // Home menu is not in nav items (so currentMenu is undefined for Home menu)
            if (currentMenu !== undefined && currentMenu.userActions !== undefined) {
                this.userActions = currentMenu.userActions;
            } else {
                this.userActions = [];
            }
        }
    }

    /** Recursively search a NavItem tree (any depth) for the item with the given id. */
    private findMenuById(items: NavItem[], menuId: string): NavItem | undefined {
        for (const item of items) {
            if (item.id === menuId) {
                return item;
            }
            if (item.children && item.children.length) {
                const found = this.findMenuById(item.children, menuId);
                if (found !== undefined) {
                    return found;
                }
            }
        }
        return undefined;
    }

    public getHomeNode(): NavItem {
        const homeNav: NavItem = new NavItem();
        homeNav.id = '0GP0_P0_C0_HOME';
        homeNav.displayName = 'Home';
        homeNav.route = 'home';
        homeNav.iconName = 'icon_home_purple_sm.svg';
        return homeNav;
    }

    public getNavItemsFromSelectedModule(): NavItem[] {
        const selectedModule = this.getSelectedModule();
        const navItems: NavItem[] = [];
        if (selectedModule !== undefined || selectedModule === null) {
            const moduleObj = this.getLocalModules();
            if (moduleObj === undefined || moduleObj === null) {
                this.navItems$.next(navItems);
                return navItems;
            }
            const currentmodule: Module = moduleObj.find(module => module.moduleName === selectedModule);
            if (currentmodule === undefined || currentmodule === null) {
                this.navItems$.next(navItems);
                return navItems;
            } else {
                this.navItems$.next(currentmodule.navItems);
                return currentmodule.navItems;
            }
        } else {
            this.navItems$.next(navItems);
            return navItems;
        }
    }

    public getNavItemsFromDefaultModule(): NavItem[] {
        const selectedModule = this.getSelectedModule();
        const navItems: NavItem[] = [];
        if (selectedModule !== undefined || selectedModule === null) {
            const moduleObj = this.getDefaultModules();
            if (moduleObj === undefined || moduleObj === null) {
                return navItems;
            }
            const currentmodule: Module = moduleObj.find(module => module.moduleName === selectedModule);
            if (currentmodule === undefined || currentmodule === null) {
                return navItems;
            } else {
                return currentmodule.navItems;
            }
        } else {
            return navItems;
        }
    }

    public getNavItemsFromModule(modules): NavItem[] {
        const selectedModule = this.getSelectedModule();
        const navItems: NavItem[] = [];
        if (selectedModule !== undefined || selectedModule === null) {
            const moduleObj = modules;
            if (moduleObj === undefined || moduleObj === null) {
                return navItems;
            }
            const currentmodule: Module = moduleObj.find(module => module.moduleName === selectedModule);
            if (currentmodule === undefined || currentmodule === null) {
                return navItems;
            } else {
                return currentmodule.navItems;
            }
        } else {
            return navItems;
        }
    }

    public getLocalModules(): Module[] {

        const selectedCustomer = this.getSelectedCustomer();

        if (this.fscAuthorizedMenusResponse === undefined || this.fscAuthorizedMenusResponse === null) {
            if (!localStorage.getItem('customer_portal_modules')) {
                return undefined;
            } else {
                this.fscAuthorizedMenusResponse = JSON.parse(localStorage.getItem('customer_portal_modules'));
            }
        }

        if (this.fclLoginService.getUserType() === 'E' || this.fclLoginService.getUserType() === 'T') {
            if (this.fscAuthorizedMenusResponse.userModulesByCustomer.length === 1) {
                this.modules = this.fscAuthorizedMenusResponse.userModulesByCustomer[0].modules;
            } else {
                for (const userModulesByCustomer of this.fscAuthorizedMenusResponse.userModulesByCustomer) {
                    if (userModulesByCustomer.cusId === selectedCustomer) {
                        this.modules = userModulesByCustomer.modules;
                        break;
                    }
                }
            }
        } else if (this.fclLoginService.getUserType() === 'I') {

            let matchFoundFlag = false;

            for (const userModulesByCustomer of this.fscAuthorizedMenusResponse.userModulesByCustomer) {
                if (userModulesByCustomer.cusId === '-1') {
                    this.defaultModules = userModulesByCustomer.modules;
                    localStorage.setItem('FlareModules', JSON.stringify(this.defaultModules));
                }
            }

            if (selectedCustomer !== null) {
                for (const userModulesByCustomer of this.fscAuthorizedMenusResponse.userModulesByCustomer) {
                    if (userModulesByCustomer.cusId === JSON.stringify(selectedCustomer.customerId)) {
                        this.modules = userModulesByCustomer.modules;
                        matchFoundFlag = true;
                    }
                }
            } else {
                this.modules = this.defaultModules;
            }


            if (matchFoundFlag === false) {
                this.modules = this.defaultModules;
            }
        }

        this.modules$.next(this.modules);
        return this.modules;
    }

    public getDefaultModules(): Module[] {

        const selectedCustomer = this.getSelectedCustomer();

        if (this.fscAuthorizedMenusResponse === undefined || this.fscAuthorizedMenusResponse === null) {
            if (!localStorage.getItem('customer_portal_modules')) {
                return undefined;
            } else {
                this.fscAuthorizedMenusResponse = JSON.parse(localStorage.getItem('customer_portal_modules'));
            }
        }

        if (this.fclLoginService.getUserType() === 'E' || this.fclLoginService.getUserType() === 'T') {
            if (this.fscAuthorizedMenusResponse.userModulesByCustomer.length === 1) {
                this.defaultModules = this.fscAuthorizedMenusResponse.userModulesByCustomer[0].modules;
            } else {
                for (const userModulesByCustomer of this.fscAuthorizedMenusResponse.userModulesByCustomer) {
                    if (userModulesByCustomer.cusId === selectedCustomer) {
                        this.defaultModules = userModulesByCustomer.modules;
                        break;
                    }
                }
            }
        } else if (this.fclLoginService.getUserType() === 'I') {

            let matchFoundFlag = false;

            for (const userModulesByCustomer of this.fscAuthorizedMenusResponse.userModulesByCustomer) {
                if (userModulesByCustomer.cusId === '-1') {
                    this.defaultModules = userModulesByCustomer.modules;
                    localStorage.setItem('FlareModules', JSON.stringify(this.defaultModules));
                }
            }
        }

        this.defaultModules$.next(this.defaultModules);
        return this.defaultModules;
    }

    public setSelectedModule(module: string) {
        this.selectedModule = module;
        localStorage.setItem('SelectedModule', this.selectedModule);
        this.getNavItemsFromSelectedModule();
    }

    public getSelectedModule(): string {
        if (this.selectedModule !== null && this.selectedModule !== undefined && this.selectedModule !== '') {
            return this.selectedModule;
        } else {
            return localStorage.getItem('SelectedModule');
        }
    }

    public clearSelectedModule() {
        this.selectedModule = '';
        localStorage.removeItem('SelectedModule');
    }

    public setModulesInLocalStorage(fscAuthorizedMenusResponse: FscAuthorizedMenusResponse) {
        this.fscAuthorizedMenusResponse = fscAuthorizedMenusResponse;
        localStorage.setItem('customer_portal_modules', JSON.stringify(this.fscAuthorizedMenusResponse));
    }

    public getFscAuthorizedMenusResponse(): FscAuthorizedMenusResponse {
        if (this.fscAuthorizedMenusResponse === undefined || this.fscAuthorizedMenusResponse === null) {
            if (!localStorage.getItem('customer_portal_modules')) {
                return undefined;
            } else {
                return JSON.parse(localStorage.getItem('customer_portal_modules'));
            }
        }
        return this.fscAuthorizedMenusResponse;
    }

    public getModulesNames(): string[] {
        if (this.fscAuthorizedMenusResponse === undefined || this.fscAuthorizedMenusResponse === null) {
            const moduleObj = this.getLocalModules();
            if (moduleObj === null || moduleObj === undefined) {
                const temp: string[] = [];
                return temp;
            }
            return moduleObj.map(module => module.moduleName);
        } else {
            return this.modules.map(module => module.moduleName);
        }
    }

    public getAuthorizedUpdatedMenus(): Observable<FscAuthorizedMenusResponse> {
        const url = `${environment.apiUserURL}/getAuthorizedMenus?device=P`;
        this.fscAuthorizedMenusResponse = undefined;
        let headers = {};
        if (this.fclLoginService.getUserType() === 'E') {
            headers = {
                'Content-Type': 'application/json',
                APPID: environment.apiBussServAPPID,
                UUID: uuid(),
                usrId: this.fclLoginService.getUser().loginId
            };
            return this.http.get<FscAuthorizedMenusResponse>(url, { headers });
        } else if (this.fclLoginService.getUserType() === 'I') {
            const accessToken$ = from(this.oktaAuth.getAccessToken());
            return accessToken$.pipe(
                concatMap(
                    (oktaTokenId) => {
                        headers = {
                            'Content-Type': 'application/json',
                            APPID: environment.apiBussServAPPID,
                            UUID: uuid(),
                            jwtToken: oktaTokenId
                        };
                        return this.http.get<FscAuthorizedMenusResponse>(url, { headers });
                    }
                )
            );
        } else if (this.fclLoginService.getUserType() === 'T') {
            const accessToken$ = from(this.oktaAuth.getAccessToken());
            return accessToken$.pipe(
                concatMap(
                    (oktaTokenId) => {
                        headers = {
                            'Content-Type': 'application/json',
                            APPID: environment.apiBussServAPPID,
                            UUID: uuid(),
                            OktaTempJWTToken: oktaTokenId
                        };
                        return this.http.get<FscAuthorizedMenusResponse>(url, { headers });
                    }
                )
            );
        }
    }

    public getAuthorizedMenus(): Observable<FscAuthorizedMenusResponse> {

        const cachedMenus = this.getFscAuthorizedMenusResponse();
        if (cachedMenus !== undefined) {
            return of(cachedMenus);
        }
        const url = `${environment.apiUserURL}/getAuthorizedMenus?device=P`;
        this.fscAuthorizedMenusResponse = undefined;
        let headers = {};
        if (this.fclLoginService.getUserType() === 'E') {
            headers = {
                'Content-Type': 'application/json',
                APPID: environment.apiBussServAPPID,
                UUID: uuid(),
                usrId: this.fclLoginService.getUser().loginId
            };
            return this.http.get<FscAuthorizedMenusResponse>(url, { headers });
        } else if (this.fclLoginService.getUserType() === 'I') {
            const accessToken$ = from(this.oktaAuth.getAccessToken());
            return accessToken$.pipe(
                concatMap(
                    (oktaTokenId) => {
                        headers = {
                            'Content-Type': 'application/json',
                            APPID: environment.apiBussServAPPID,
                            UUID: uuid(),
                            jwtToken: oktaTokenId
                        };
                        return this.http.get<FscAuthorizedMenusResponse>(url, { headers });
                    }
                )
            );
        } else if (this.fclLoginService.getUserType() === 'T') {
            const accessToken$ = from(this.oktaAuth.getAccessToken());
            return accessToken$.pipe(
                concatMap(
                    (oktaTokenId) => {
                        headers = {
                            'Content-Type': 'application/json',
                            APPID: environment.apiBussServAPPID,
                            UUID: uuid(),
                            OktaTempJWTToken: oktaTokenId
                        };
                        return this.http.get<FscAuthorizedMenusResponse>(url, { headers });
                    }
                )
            );
        }
    }

    public setCustomersInLocalStorage(customers: Customers[]) {
        this.customers = customers;
        localStorage.setItem('external_user_customers', JSON.stringify(this.customers));
    }

    public getCustomersInLocalStorage(): Customers[] {
        if (!localStorage.getItem('external_user_customers')) {
            return undefined;
        } else {
            return JSON.parse(localStorage.getItem('external_user_customers'));
        }
    }

    public setSelectedCustomer(selectedCustomer: Customers) {
        localStorage.setItem('selectedCustomer', JSON.stringify(selectedCustomer));
        this.selectedCustomer = JSON.parse(localStorage.getItem('selectedCustomer'));
    }

    public removeCustomer() {
        this.selectedCustomer = undefined;
        localStorage.removeItem('selectedCustomer');
        this.selectedCustomer = JSON.parse(localStorage.getItem('selectedCustomer'));
    }

    public getSelectedCustomer(): Customers {
        if (this.selectedCustomer === null || this.selectedCustomer === undefined) {
            this.selectedCustomer = JSON.parse(localStorage.getItem('selectedCustomer'));
            return this.selectedCustomer;
        } else {
            return this.selectedCustomer;
        }
    }

    public setSelectedBusinessFacility(selectedBuFacility: BuFacility) {
        localStorage.setItem('selectedBusinessFacility', JSON.stringify(selectedBuFacility));
        this.selectedBusinessFacility = JSON.parse(localStorage.getItem('selectedBusinessFacility'));
        this.setSelectedFacility(selectedBuFacility.facility);
        this.setSelectedBusinessUnit(selectedBuFacility.bu)
    }

    public getSelectedBusinessFacility(): BuFacility {
        if (this.selectedBusinessFacility === null || this.selectedBusinessFacility === undefined) {
            this.selectedBusinessFacility = JSON.parse(localStorage.getItem('selectedBusinessFacility'));
            return this.selectedBusinessFacility;
        } else {
            return this.selectedBusinessFacility;
        }
    }

    public removeBusinessFacility() {
        this.selectedBusinessFacility = undefined;
        localStorage.removeItem('selectedBusinessFacility');
        this.selectedBusinessFacility = JSON.parse(localStorage.getItem('selectedBusinessFacility'));
        this.removeFacility();
        this.removeBusinessUnit();
    }

    public setSelectedBusinessUnit(selectedBU: BusinessUnits) {
        localStorage.setItem('selectedBusinessUnit', JSON.stringify(selectedBU));
        this.selectedBusinessUnit = JSON.parse(localStorage.getItem('selectedBusinessUnit'));
    }

    public getSelectedBusinessUnit(): BusinessUnits {
        if (this.selectedBusinessUnit === null || this.selectedBusinessUnit === undefined) {
            this.selectedBusinessUnit = JSON.parse(localStorage.getItem('selectedBusinessUnit'));
            return this.selectedBusinessUnit;
        } else {
            return this.selectedBusinessUnit;
        }
    }

    public removeBusinessUnit() {
        this.selectedBusinessUnit = undefined;
        localStorage.removeItem('selectedBusinessUnit');
        this.selectedBusinessUnit = JSON.parse(localStorage.getItem('selectedBusinessUnit'));
        this.removeFacility();
    }

    public getSelectedFacility(): Facilities {
        if (this.selectedFacility === null || this.selectedFacility === undefined) {
            this.selectedFacility = JSON.parse(localStorage.getItem('selectedFacility'));
            return this.selectedFacility;
        } else {
            return this.selectedFacility;
        }
    }

    public setSelectedFacility(selectedFacility: Facilities) {
        localStorage.setItem('selectedFacility', JSON.stringify(selectedFacility));
        this.selectedFacility = JSON.parse(localStorage.getItem('selectedFacility'));
    }

    public removeFacility() {
        this.selectedFacility = undefined;
        localStorage.removeItem('selectedFacility');
        this.selectedFacility = JSON.parse(localStorage.getItem('selectedFacility'));
    }

    getCustomers(): Observable<CustomerResponse> {

        // if (this.fclLoginService.getUserType() === 'E') {
        const fscAuthorizedMenusResponse: FscAuthorizedMenusResponse = this.getFscAuthorizedMenusResponse();
        const customerId: number[] = [];        
        if (fscAuthorizedMenusResponse !== undefined && fscAuthorizedMenusResponse.userCustomers !== undefined
            && fscAuthorizedMenusResponse.userCustomers.length > 0) {
            for (const userCustomer of fscAuthorizedMenusResponse.userCustomers) {
                let deActivate = false;
                for (const userFacilityBusiness of userCustomer.userFacilityBusinessUnits) {
                    if (userFacilityBusiness.status === '0') {
                        deActivate = true;                        
                    }
                }
                if (!deActivate) {
                    customerId.push(userCustomer.custId);
                }
            }
        }
        if (customerId.length > 0) {
            const customer = new Customers();
            return this.customerService.getCustomerIdsInSummary('0', '500', customerId.toString(), 'details');
        } else {
            return of(new CustomerResponse());
        }
    }

    hasPermission(action: string): boolean {
        if (this.userActions === undefined || this.userActions.length === 0) {
            return false;
        }
        return this.userActions.find(act => act.userAction === action) !== undefined;
    }

    getUserType() {
        if (this.userType !== undefined) {
            return this.userType;
        } else {
            return localStorage.getItem('userType');
        }
    }

    getUserId() {

        if (this.getUserType() === 'I') {
            return this.fclLoginService.getUser().fedExId;
        } else if (this.getUserType() === 'E' || this.fclLoginService.getUserType() === 'T') {
            return this.fclLoginService.getUser().loginId;
        }

    }

    set custList(customerList) {
        this.customers = customerList;
    }

    get custList() {
        return this.customers;
    }

    set facAccessList(custID) {
        const buFacWAccess = [];
        let buFacility = new BuFacility();
        if (localStorage.getItem('customer_portal_modules') !== undefined && localStorage.getItem('customer_portal_modules') !== null) {
            const buList = JSON.parse(localStorage.getItem('customer_portal_modules')).userCustomers.filter(x => x.custId === custID);

            for (const bu of buList) {
                for (const fac of bu.userFacilityBusinessUnits) {
                    buFacility.lookupCode = `${fac.businessUnitId}` + '~~' + `${fac.facilityId}`;
                    buFacility.lookupCodeDescription = fac.businessUnitName + '~~' + fac.facilityName;
                    buFacility.bu = bu;
                    buFacility.facility = fac;
                    buFacWAccess.push(buFacility);
                    buFacility = new BuFacility();
                }
            }
        }
        /** removes non unique values */
        let buFacwAccessArr = buFacWAccess.filter((item, i, ar) => ar.indexOf(item) === i);
        buFacwAccessArr = buFacwAccessArr.filter((test, index, array) =>
            index === array.findIndex((findTest) =>
                findTest.lookupCode === test.lookupCode
            )
        );

        this.buFacwAccessArr = buFacwAccessArr;
    }

    get facAccessList() {
        return this.buFacwAccessArr;
    }

    homeComponentClick(routeUrl) {
        routeUrl = routeUrl.substring(1);
        const selectedModule = this.getSelectedModule();
        const moduleObj: Module[] = this.getLocalModules();
        this.userActions = [];
        let currentMenu: NavItem;

        if (moduleObj !== undefined) {
            const currentmodule: Module = moduleObj.find(module => module.moduleName === selectedModule);
            // Recurse the FULL navItem tree (any depth) — same fix as setMenuPermissions,
            // this used to only check the top-level items + one level of children.
            currentMenu = currentmodule?.navItems !== undefined
                ? this.findMenuByRoute(currentmodule.navItems, routeUrl)
                : undefined;
            if (currentMenu === undefined) {
                for (const mod of moduleObj) {
                    if (mod.navItems === undefined) { continue; }
                    currentMenu = this.findMenuByRoute(mod.navItems, routeUrl);
                    if (currentMenu !== undefined) { break; }
                }
            }
            if (currentMenu !== undefined && currentMenu.userActions !== undefined) {
                this.userActions = currentMenu.userActions;
                this.hasPermission(currentMenu.id);
            } else {
                this.userActions = [];
            }
        }

        return currentMenu?.id
    }

    /** Recursively search a NavItem tree (any depth) for the item with the given route. */
    private findMenuByRoute(items: NavItem[], routeUrl: string): NavItem | undefined {
        for (const item of items) {
            if (item.route === routeUrl) {
                return item;
            }
            if (item.children && item.children.length) {
                const found = this.findMenuByRoute(item.children, routeUrl);
                if (found !== undefined) {
                    return found;
                }
            }
        }
        return undefined;
    }

    deleteAllCookies() {
        const cookies = document.cookie.split(';');
        for (const cookie of cookies) {
            const cookieName = cookie.split('=')[0];
            if (cookieName !== 'okta-oauth-state' && cookieName !== 'okta-oauth-nonce') {
                document.cookie = cookieName + "=;domain=.fedex.com;path=/;max-age=0";
                document.cookie = cookieName + "=;domain=.logistics.fedex.com;path=/;max-age=0";    
            }
        }
    }
}